import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { expenseData, totalExpenses } from '../data/expenses';
import { ArrowLeft } from 'lucide-react';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#d0ed57'];

const ExpenseReport: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [drilldownCategory, setDrilldownCategory] = useState<string | null>(null);
  const [drilldownLocation, setDrilldownLocation] = useState<string | null>(null);
  
  const locations = Object.keys(totalExpenses.locations).filter(
    loc => loc !== 'Overhead' && loc !== 'Admin' && loc !== 'Billing' && loc !== 'Marketing'
  );
  
  const categories = expenseData.map(item => item.category);
  
  const getPieData = () => {
    if (selectedLocation === 'all') {
      return expenseData.map(item => ({
        name: item.category,
        value: item.total
      }));
    } else {
      return expenseData.map(item => ({
        name: item.category,
        value: item.locations[selectedLocation] || 0
      })).filter(item => item.value > 0);
    }
  };
  
  const getBarData = () => {
    if (selectedCategory === 'all') {
      return locations.map(location => {
        const data: { [key: string]: any } = { name: location };
        expenseData.forEach(expense => {
          data[expense.category] = expense.locations[location] || 0;
        });
        return data;
      });
    } else {
      const expense = expenseData.find(item => item.category === selectedCategory);
      if (!expense) return [];
      
      return locations.map(location => ({
        name: location,
        value: expense.locations[location] || 0
      }));
    }
  };

  // Get detailed breakdown for a specific category and location
  const getDrilldownData = () => {
    if (!drilldownCategory) return [];
    
    const categoryData = expenseData.find(item => item.category === drilldownCategory);
    if (!categoryData) return [];
    
    if (drilldownLocation) {
      // If we have both category and location, we'd need more detailed data
      // For this example, we'll create some mock detailed data
      return generateMockDetailedData(drilldownCategory, drilldownLocation);
    } else {
      // Just show the breakdown by location for this category
      return Object.entries(categoryData.locations)
        .map(([location, amount]) => ({
          name: location,
          value: amount
        }))
        .filter(item => item.value > 0)
        .sort((a, b) => b.value - a.value);
    }
  };
  
  // Generate mock detailed data for demonstration purposes
  const generateMockDetailedData = (category: string, location: string) => {
    const categoryData = expenseData.find(item => item.category === category);
    if (!categoryData) return [];
    
    const totalAmount = categoryData.locations[location] || 0;
    
    // Create mock subcategories based on the expense category
    let subcategories: {name: string, value: number}[] = [];
    
    if (category === "Labor") {
      const total = totalAmount;
      subcategories = [
        { name: "Full-time Staff", value: total * 0.65 },
        { name: "Part-time Staff", value: total * 0.25 },
        { name: "Overtime", value: total * 0.05 },
        { name: "Benefits", value: total * 0.05 }
      ];
    } else if (category === "Occupancy") {
      subcategories = [
        { name: "Rent", value: totalAmount * 0.8 },
        { name: "Maintenance", value: totalAmount * 0.1 },
        { name: "Property Tax", value: totalAmount * 0.1 }
      ];
    } else if (category === "Office Expenses") {
      subcategories = [
        { name: "Supplies", value: totalAmount * 0.4 },
        { name: "Equipment", value: totalAmount * 0.3 },
        { name: "Printing", value: totalAmount * 0.2 },
        { name: "Postage", value: totalAmount * 0.1 }
      ];
    } else if (category === "Utilities") {
      subcategories = [
        { name: "Electricity", value: totalAmount * 0.5 },
        { name: "Water", value: totalAmount * 0.1 },
        { name: "Internet", value: totalAmount * 0.2 },
        { name: "Phone", value: totalAmount * 0.2 }
      ];
    } else if (category === "Marketing") {
      subcategories = [
        { name: "Digital Ads", value: totalAmount * 0.4 },
        { name: "Print Media", value: totalAmount * 0.2 },
        { name: "Events", value: totalAmount * 0.3 },
        { name: "Other", value: totalAmount * 0.1 }
      ];
    } else if (category === "Insurance") {
      subcategories = [
        { name: "Liability", value: totalAmount * 0.5 },
        { name: "Property", value: totalAmount * 0.3 },
        { name: "Workers Comp", value: totalAmount * 0.2 }
      ];
    } else if (category === "EMR Software") {
      subcategories = [
        { name: "Licenses", value: totalAmount * 0.7 },
        { name: "Support", value: totalAmount * 0.2 },
        { name: "Training", value: totalAmount * 0.1 }
      ];
    } else {
      subcategories = [
        { name: "Miscellaneous", value: totalAmount }
      ];
    }
    
    return subcategories;
  };

  // Handle clicking on a category in the table
  const handleCategoryClick = (category: string) => {
    setDrilldownCategory(category);
    setDrilldownLocation(null);
  };

  // Handle clicking on a location for a specific category
  const handleLocationClick = (location: string) => {
    if (drilldownCategory) {
      setDrilldownLocation(location);
    }
  };

  // Handle going back from drilldown view
  const handleBackClick = () => {
    if (drilldownLocation) {
      setDrilldownLocation(null);
    } else if (drilldownCategory) {
      setDrilldownCategory(null);
    }
  };

  // Get the title for the drilldown view
  const getDrilldownTitle = () => {
    if (drilldownCategory && drilldownLocation) {
      return `${drilldownCategory} Expenses - ${drilldownLocation}`;
    } else if (drilldownCategory) {
      return `${drilldownCategory} Expenses by Location`;
    }
    return "";
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6">Expense Analysis</h1>
      
      {/* Drilldown View */}
      {drilldownCategory && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center mb-4">
            <button 
              onClick={handleBackClick}
              className="mr-4 p-2 rounded-full hover:bg-gray-100"
              title="Go back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h2 className="text-xl font-bold">{getDrilldownTitle()}</h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getDrilldownData()}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {getDrilldownData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            <div>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white">
                  <thead>
                    <tr>
                      <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        {drilldownLocation ? "Subcategory" : "Location"}
                      </th>
                      <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Percentage
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getDrilldownData().map((item, index) => {
                      const totalValue = getDrilldownData().reduce((sum, i) => sum + i.value, 0);
                      const percentage = (item.value / totalValue) * 100;
                      
                      return (
                        <tr 
                          key={item.name} 
                          className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                          onClick={() => !drilldownLocation && handleLocationClick(item.name)}
                          style={{ cursor: !drilldownLocation ? 'pointer' : 'default' }}
                        >
                          <td className="py-2 px-4 border-b border-gray-200 font-medium">{item.name}</td>
                          <td className="py-2 px-4 border-b border-gray-200">${item.value.toLocaleString()}</td>
                          <td className="py-2 px-4 border-b border-gray-200">{percentage.toFixed(2)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50 font-semibold">
                    <tr>
                      <td className="py-2 px-4 border-t border-gray-200">Total</td>
                      <td className="py-2 px-4 border-t border-gray-200">
                        ${getDrilldownData().reduce((sum, item) => sum + item.value, 0).toLocaleString()}
                      </td>
                      <td className="py-2 px-4 border-t border-gray-200">100%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main View (only shown when not in drilldown) */}
      {!drilldownCategory && (
        <>
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex flex-col md:flex-row justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Expense Distribution</h2>
              <div className="mt-4 md:mt-0">
                <select
                  className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 mr-2"
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                >
                  <option value="all">All Locations</option>
                  {locations.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                  <option value="Overhead">Overhead</option>
                  <option value="Admin">Admin</option>
                  <option value="Billing">Billing</option>
                  <option value="Marketing">Marketing</option>
                </select>
              </div>
            </div>
            
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getPieData()}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {getPieData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex flex-col md:flex-row justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Expense Breakdown by Location</h2>
              <div className="mt-4 md:mt-0">
                <select
                  className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                {selectedCategory === 'all' ? (
                  <BarChart
                    data={getBarData()}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                    <Legend />
                    {categories.map((category, index) => (
                      <Bar key={category} dataKey={category} name={category} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </BarChart>
                ) : (
                  <BarChart
                    data={getBarData()}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                    <Legend />
                    <Bar dataKey="value" name={selectedCategory} fill={COLORS[0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-4">Expense Details</h2>
        
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Category
                </th>
                {locations.map(location => (
                  <th key={location} className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {location}
                  </th>
                ))}
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Overhead
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {expenseData.map((expense) => (
                <tr key={expense.category}>
                  <td 
                    className="py-2 px-4 border-b border-gray-200 font-medium cursor-pointer hover:text-blue-600"
                    onClick={() => handleCategoryClick(expense.category)}
                  >
                    {expense.category}
                  </td>
                  {locations.map(location => (
                    <td 
                      key={location} 
                      className="py-2 px-4 border-b border-gray-200 cursor-pointer hover:text-blue-600"
                      onClick={() => {
                        setDrilldownCategory(expense.category);
                        setDrilldownLocation(location);
                      }}
                    >
                      ${(expense.locations[location] || 0).toLocaleString()}
                    </td>
                  ))}
                  <td 
                    className="py-2 px-4 border-b border-gray-200 cursor-pointer hover:text-blue-600"
                    onClick={() => {
                      setDrilldownCategory(expense.category);
                      setDrilldownLocation('Overhead');
                    }}
                  >
                    ${(expense.locations['Overhead'] || 0).toLocaleString()}
                  </td>
                  <td className="py-2 px-4 border-b border-gray-200 font-semibold">
                    ${expense.total.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 font-semibold">
              <tr>
                <td className="py-2 px-4 border-b border-gray-200">TOTAL</td>
                {locations.map(location => (
                  <td key={location} className="py-2 px-4 border-b border-gray-200">
                    ${totalExpenses.locations[location].toLocaleString()}
                  </td>
                ))}
                <td className="py-2 px-4 border-b border-gray-200">
                  ${totalExpenses.locations['Overhead'].toLocaleString()}
                </td>
                <td className="py-2 px-4 border-b border-gray-200">
                  ${totalExpenses.total.toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ExpenseReport;